import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconBinaryTree,
  IconChecklist,
  IconListCheck,
  IconPackage,
  IconStack2,
} from '@tabler/icons-react';
import styles from '../ReportMetrics.module.scss';
import detailStyles from '../widgets/Detail.module.scss';
import { KpiScope } from '../../../api/model';
import {
  HEALTH_DIMENSIONS,
  KPI,
  categorical,
  categoricalByScopeId,
  formatDelta,
  healthKeyFromStatus,
  numeric,
} from '../intelligence';
import {
  Area,
  TONE_COLOR,
  actionState,
  ACTION_STATE_TONE,
  byUrgency,
  actionTitle,
  isOpenAction,
  checkpointRows,
  deliverableRows,
  dependencyRows,
  isHigh,
  mainDrivers,
  scoreBand,
  withinDays,
  isoDay,
} from '../detail';
import type { ReportTab } from '../ReportMetrics';
import type { ReportViewProps } from './viewProps';
import { count, formatDay, pct } from './tiles';
import ChartCard from '../widgets/ChartCard';
import ScoreRing from '../widgets/ScoreRing';
import KpiHistoryChart from '../widgets/KpiHistoryChart';
import MetricTile from '../widgets/MetricTile';
import StatusBadge from '../widgets/StatusBadge';
import DataTable from '../widgets/DataTable';
import Segmented from '../widgets/Segmented';
import InlineBar from '../widgets/InlineBar';
import { useTenantUsers } from '../../../hooks/useTenantUsers';

const AREA_TAB: Record<Area, ReportTab> = {
  execution: 'execution',
  readiness: 'readiness',
  quality: 'quality',
  dependencies: 'dependencies',
};

const DIMENSION_TAB: Record<string, ReportTab> = {
  execution: 'execution',
  quality: 'quality',
  readiness: 'readiness',
  dependency: 'dependencies',
};

type Range = '84' | '182' | '365';

/** Phase states that mean "this is where the project is working now". */
const ACTIVE_PHASE = new Set(['IN_PROGRESS', 'IN_REVIEW', 'DELAYED']);

/**
 * Übersicht (Detailmasken §1) - replaces the Executive Summary: how the project
 * stands overall, which dimensions drive that, what is behind it and where
 * action is needed. Every block links on to the page that analyses it.
 */
const Overview: React.FC<ReportViewProps> = ({
  project,
  kpis,
  historyIndex,
  dependencyGraph,
  findings,
  actionPoints,
  index,
  phaseNames,
  openTab,
  openObject,
}) => {
  const { t } = useTranslation(['templates', 'gantt']);
  const { namesByIds } = useTenantUsers();
  const [range, setRange] = useState<Range>('84');

  const healthScore = numeric(index, KPI.projectHealthScore);
  const healthKey =
    healthKeyFromStatus(categorical(index, KPI.projectHealthStatus)) ?? scoreBand(healthScore)?.key;
  const healthColor = healthKey ? TONE_COLOR[scoreBand(healthScore)?.tone ?? 'neutral'] : TONE_COLOR.neutral;
  const trend = formatDelta(numeric(index, KPI.projectHealthTrend));
  const confidence = numeric(index, KPI.healthConfidence);

  const deliverables = useMemo(
    () => deliverableRows(findings?.deliverables, kpis, phaseNames),
    [findings, kpis, phaseNames],
  );
  const checkpoints = useMemo(
    () => checkpointRows(findings?.openCheckpoints, findings?.deliverables, phaseNames),
    [findings, phaseNames],
  );
  const dependencies = useMemo(() => dependencyRows(dependencyGraph), [dependencyGraph]);
  const drivers = useMemo(
    () => mainDrivers(deliverables, checkpoints, dependencies, index),
    [deliverables, checkpoints, dependencies, index],
  );

  const healthPoints = useMemo(
    () => withinDays(historyIndex.get(KPI.projectHealthScore), Number(range)),
    [historyIndex, range],
  );
  // the latest value and how far it moved over the selected range
  const healthRangeDelta =
    healthPoints.length > 1 ? Math.round(healthPoints[healthPoints.length - 1].value - healthPoints[0].value) : undefined;

  // Project DNA: what kind of project this is, as context for the numbers.
  const currentPhase = useMemo(() => {
    const statuses = categoricalByScopeId(kpis, KPI.phaseStatus, KpiScope.PHASE);
    const ids = [...phaseNames.keys()];
    const active = ids.find((id) => ACTIVE_PHASE.has(statuses.get(id) ?? ''));
    const next = ids.find((id) => statuses.get(id) === 'NOT_STARTED');
    const id = active ?? next;
    return id != null ? phaseNames.get(id) : undefined;
  }, [kpis, phaseNames]);

  const structure = useMemo(() => {
    const rows = findings?.deliverables ?? [];
    return {
      phases: phaseNames.size,
      deliverables: rows.length,
      tasks: rows.reduce((sum, d) => sum + (d.taskCount ?? 0), 0),
      checkpoints: rows.reduce((sum, d) => sum + (d.checkpointCount ?? 0), 0),
      dependencies: dependencyGraph?.edges?.length ?? 0,
      high: rows.filter((d) => isHigh(d.criticality)).length,
    };
  }, [findings, phaseNames, dependencyGraph]);

  const today = isoDay(new Date());
  const openActions = useMemo(() => (actionPoints ?? []).filter(isOpenAction), [actionPoints]);
  const overdueActions = openActions.filter((p) => actionState(p, today) === 'overdue').length;
  const delayedCheckpoints = checkpoints.filter((cp) => cp.delayed).length;
  const violations = dependencies.filter((d) => d.state === 'VIOLATED').length;
  const unassigned = deliverables.reduce((sum, d) => sum + (d.unassignedTaskCount ?? 0), 0);
  const overdueDeliverables = numeric(index, KPI.overdueDeliverableCount);

  // Nächste Schritte: the open Action Points most in need of attention - the
  // rules raise their own now, so there is nothing else to fill in with.
  const nextSteps = useMemo(() => byUrgency(openActions).slice(0, 5), [openActions]);

  const methodLabel = project?.method ? t(`report.detail.method.${project.method}`, project.method) : '—';

  return (
    <div className={styles.dashboard}>
      <div className={styles.chartGrid}>
        <ChartCard title={t('report.health.title')} fitContent>
          <div className={detailStyles.twoColumns}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <ScoreRing value={healthScore} color={healthColor} size={120} thickness={10} />
              {healthKey && (
                <StatusBadge tone={scoreBand(healthScore)?.tone} label={t(`report.health.status.${healthKey}`)} />
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <span className={detailStyles.tileSub}>{t('report.cockpit.trend')}</span>
                <div
                  className={
                    trend.direction === 'up'
                      ? detailStyles.good
                      : trend.direction === 'down'
                        ? detailStyles.bad
                        : detailStyles.flat
                  }
                  style={{ fontWeight: 700, fontSize: 18 }}
                >
                  {trend.delta}
                </div>
              </div>
              <div>
                <span className={detailStyles.tileSub}>{t('report.confidence.title')}</span>
                <InlineBar value={confidence} />
              </div>
            </div>
          </div>
          {healthKey && <p className={detailStyles.note}>{t(`report.detail.overview.assessment.${healthKey}`)}</p>}
        </ChartCard>

        <ChartCard title={t('report.dimensions.title')} subtitle={t('report.detail.overview.dimensionsHint')} fitContent>
          <div className={detailStyles.ringRow}>
            {HEALTH_DIMENSIONS.map((dimension) => {
              const value = numeric(index, dimension.key);
              const band = scoreBand(value);
              const dimensionConfidence = numeric(index, dimension.confidenceKey);
              return (
                <button
                  key={dimension.key}
                  type="button"
                  className={detailStyles.ringCell}
                  onClick={() => openTab?.(DIMENSION_TAB[dimension.labelKey])}
                >
                  <span className={detailStyles.ringLabel}>{t(`report.dimensions.${dimension.labelKey}`)}</span>
                  <ScoreRing value={value} color={TONE_COLOR[band?.tone ?? 'neutral']} size={64} thickness={6} />
                  {band && <StatusBadge tone={band.tone} label={t(`report.health.status.${band.key}`)} />}
                  <span className={detailStyles.tileSub}>
                    {t('report.detail.overview.confidenceShort', { value: pct(dimensionConfidence) })}
                  </span>
                </button>
              );
            })}
          </div>
        </ChartCard>

        <ChartCard
          title={t('report.detail.overview.healthTrend')}
          subtitle={t('report.detail.overview.healthTrendHint')}
          action={
            <Segmented<Range>
              value={range}
              onChange={setRange}
              options={[
                { value: '84', label: t('report.detail.range.weeks12') },
                { value: '182', label: t('report.detail.range.months6') },
                { value: '365', label: t('report.detail.range.months12') },
              ]}
            />
          }
        >
          {healthPoints.length > 0 ? (
            <>
              <div className={detailStyles.tileValueRow}>
                <span className={detailStyles.tileValue}>{Math.round(healthPoints[healthPoints.length - 1].value)}</span>
                <span className={detailStyles.tileSub}>/ 100</span>
                {healthRangeDelta != null && (
                  <span
                    className={healthRangeDelta > 0 ? detailStyles.good : healthRangeDelta < 0 ? detailStyles.bad : detailStyles.flat}
                    style={{ fontWeight: 600, fontSize: 13 }}
                  >
                    {t('report.detail.overview.healthRangeDelta', {
                      value: `${healthRangeDelta > 0 ? '+' : ''}${healthRangeDelta}`,
                    })}
                  </span>
                )}
              </div>
              <KpiHistoryChart data={healthPoints} seriesName={t('report.health.title')} max={100} />
            </>
          ) : (
            <p className={styles.planDescription}>{t('report.trendScore.noHistory')}</p>
          )}
        </ChartCard>
      </div>

      <div className={detailStyles.twoColumns}>
        <ChartCard title={t('report.detail.overview.dna')} subtitle={t('report.detail.overview.dnaHint')} fitContent>
          <div className={detailStyles.twoColumns}>
            <dl className={detailStyles.facts}>
              <dt>{t('report.detail.overview.projectType')}</dt>
              <dd>{project?.projectType || '—'}</dd>
              <dt>{t('report.detail.overview.method')}</dt>
              <dd>{methodLabel}</dd>
              <dt>{t('report.detail.overview.currentPhase')}</dt>
              <dd>{currentPhase ?? '—'}</dd>
              <dt>{t('report.detail.overview.start')}</dt>
              <dd>{formatDay(project?.startTime)}</dd>
              <dt>{t('report.detail.overview.plannedEnd')}</dt>
              <dd>{formatDay(project?.endTime)}</dd>
            </dl>
            <div className={detailStyles.counts}>
              {[
                { icon: IconStack2, value: structure.phases, label: t('report.detail.overview.count.phases') },
                { icon: IconPackage, value: structure.deliverables, label: t('report.detail.overview.count.deliverables') },
                { icon: IconListCheck, value: structure.tasks, label: t('report.detail.overview.count.tasks') },
                { icon: IconChecklist, value: structure.checkpoints, label: t('report.detail.overview.count.checkpoints') },
                { icon: IconBinaryTree, value: structure.dependencies, label: t('report.detail.overview.count.dependencies') },
                {
                  icon: IconAlertTriangle,
                  value: structure.high,
                  label: t('report.detail.overview.count.high'),
                  critical: structure.high > 0,
                },
              ].map(({ icon: Icon, value, label, critical }) => (
                <div key={label} className={`${detailStyles.countRow} ${critical ? detailStyles.countCritical : ''}`}>
                  <Icon size={18} stroke={1.8} />
                  <span className={detailStyles.countValue}>{value}</span>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>

        <ChartCard title={t('report.detail.overview.quickFacts')} subtitle={t('report.detail.overview.quickFactsHint')} fitContent>
          <div className={styles.tileRow}>
            <MetricTile
              label={t('report.kpi.projectProgress.label')}
              value={pct(numeric(index, KPI.projectProgress))}
              bar={{ value: numeric(index, KPI.projectProgress) }}
              onClick={() => openTab?.('execution')}
            />
            <MetricTile
              label={t('report.detail.overview.openActions')}
              value={`${openActions.length}`}
              sub={overdueActions > 0 ? t('report.detail.overview.ofWhichOverdue', { count: overdueActions }) : undefined}
              status={overdueActions > 0 ? { tone: 'critical', label: t('report.detail.actions.state.overdue') } : undefined}
              onClick={() => openTab?.('actions')}
            />
            <MetricTile
              label={t('report.cockpit.overdueDeliverables')}
              value={count(overdueDeliverables)}
              status={(overdueDeliverables ?? 0) > 0 ? { tone: 'critical', label: t('report.detail.attention') } : undefined}
              onClick={() => openTab?.('execution')}
            />
            <MetricTile
              label={t('report.detail.overview.delayedCheckpoints')}
              value={`${delayedCheckpoints}`}
              status={delayedCheckpoints > 0 ? { tone: 'critical', label: t('report.detail.attention') } : undefined}
              onClick={() => openTab?.('quality')}
            />
            <MetricTile
              label={t('report.detail.overview.hardViolations')}
              value={`${violations}`}
              status={violations > 0 ? { tone: 'critical', label: t('report.detail.attention') } : undefined}
              onClick={() => openTab?.('dependencies')}
            />
            <MetricTile
              label={t('report.detail.overview.unassignedTasks')}
              value={`${unassigned}`}
              status={unassigned > 0 ? { tone: 'warning', label: t('report.detail.attention') } : undefined}
              onClick={() => openTab?.('readiness')}
            />
          </div>
        </ChartCard>
      </div>

      <div className={detailStyles.twoColumns}>
        <ChartCard title={t('report.detail.overview.drivers')} subtitle={t('report.detail.overview.driversHint')} fitContent>
          <DataTable
            rows={drivers}
            rowKey={(d) => d.id}
            empty={t('report.detail.overview.noDrivers')}
            onRowClick={(d) => (d.target ? openObject?.(d.target) : openTab?.(AREA_TAB[d.area]))}
            columns={[
              {
                key: 'theme',
                header: t('report.detail.col.theme'),
                render: (d) => t(`report.detail.driver.${d.kind}.theme`, d.params),
              },
              { key: 'area', header: t('report.detail.col.area'), render: (d) => t(`report.detail.area.${d.area}`) },
              {
                key: 'impact',
                header: t('report.detail.col.impact'),
                render: (d) => (
                  <span className={d.tone === 'critical' ? detailStyles.critical : detailStyles.warning}>
                    {t(`report.detail.driver.${d.kind}.impact`, d.params)}
                  </span>
                ),
              },
              {
                key: 'status',
                header: t('report.detail.col.status'),
                render: (d) => (
                  <StatusBadge
                    tone={d.tone}
                    label={t(d.tone === 'critical' ? 'report.severity.CRITICAL' : 'report.detail.attention')}
                  />
                ),
              },
            ]}
          />
        </ChartCard>

        <ChartCard
          title={t('report.detail.overview.nextSteps')}
          subtitle={t('report.detail.overview.nextStepsHint')}
          action={
            <button type="button" className={detailStyles.linkButton} onClick={() => openTab?.('actions')}>
              {t('report.detail.toActions')}
              <IconArrowRight size={14} stroke={2} />
            </button>
          }
          fitContent
        >
          {nextSteps.length === 0 ? (
            <div className={detailStyles.empty}>{t('report.detail.actions.none')}</div>
          ) : (
            <div className={detailStyles.counts}>
              {nextSteps.map((point) => {
                const state = actionState(point, today);
                return (
                  <div
                    key={`ap-${point.id}`}
                    className={`${detailStyles.tileFooter} ${detailStyles.clickableRow}`}
                    style={{ padding: '6px 0', cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onClick={() => point.id != null && openObject?.({ kind: 'actionPoint', id: point.id })}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && point.id != null && openObject?.({ kind: 'actionPoint', id: point.id })
                    }
                  >
                    <span style={{ minWidth: 0 }}>
                      <StatusBadge tone={ACTION_STATE_TONE[state]} label={actionTitle(t, point, phaseNames)} />
                      <span className={detailStyles.tileSub} style={{ display: 'block', paddingLeft: 14 }}>
                        {namesByIds(point.assignedUserIds ? [...point.assignedUserIds] : []) ||
                          t('report.detail.actions.noOwner')}
                      </span>
                    </span>
                    <span className={state === 'overdue' ? detailStyles.critical : detailStyles.muted}>
                      {formatDay(point.validationDate)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

export default Overview;
